import { useState, useMemo, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import React from "react";
import Select, { components } from "react-select";
import axios from "axios";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import { numeroPerExcel, scaricaExcel, FMT_EURO, FMT_PERC } from "../exportExcel";
import "./Ricavi.css";
import "../../OperazioniAutomezzi/ControlloKmOre/ControlloKmOre.css";
import "../../HrGenerale/TrendKpi/TrendKpi.css";
import Lf23Loader from "../../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

// ── Opzioni dropdown ──
const colonneOptions = [
    { value: "anno", label: "Anno" },
    { value: "trimestre", label: "Trimestre" },
    { value: "mese", label: "Mese" },
];

const righeOptions = [
    { value: "macro_voce_analitica", label: "Macro Voce Analitica" },
    { value: "albero3", label: "Albero 3" },
    { value: "des_conto", label: "Conto" },
    { value: "des_partner", label: "Cliente/Fornitore" },
];

const indicatoreOptions = [
    { value: "ricavi", label: "Ricavi" },
    { value: "ricavi_yoy", label: "Ricavi YoY" },
];

// ── Formatters ──
// Sotto 1Mln formatta in K (interi). Da 1Mln in su passa a "X,X Mln €" (1 decimale).
const fmtEuroK = (v) => {
    if (v == null) return "";
    if (v === 0) return "0K €";
    if (Math.abs(v) >= 1_000_000) {
        return `${(v / 1_000_000).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}Mln €`;
    }
    const k = v / 1000;
    return `${Math.round(k).toLocaleString("it-IT")}K €`;
};

const fmtPerc = (v) => {
    if (v == null) return "";
    return `${v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

const fmtEuroCompact = (v) => {
    if (v == null) return "—";
    if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toLocaleString("it-IT", { maximumFractionDigits: 1 }) + "Mln €";
    if (Math.abs(v) >= 1_000) return (v / 1_000).toLocaleString("it-IT", { maximumFractionDigits: 0 }) + "K €";
    return v.toLocaleString("it-IT", { maximumFractionDigits: 0 }) + " €";
};

const nomiMesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const meseToTrimestre = { 1: 1, 2: 1, 3: 1, 4: 2, 5: 2, 6: 2, 7: 3, 8: 3, 9: 3, 10: 4, 11: 4, 12: 4 };

// Helper YoY: pattern già usato in TrendKpi.js
const calcYoY = (curr, prev) => {
    if (curr == null || prev == null || prev === 0) return null;
    return Math.round(((curr - prev) / Math.abs(prev)) * 1000) / 10;
};

// ── React-select: mostra max 2 valori, poi badge +N ──
const MultiValueContainer = ({ children, selectProps, data, ...props }) => {
    const values = selectProps.value || [];
    const index = values.findIndex(v => v.value === data.value);
    if (index < 2) {
        return <components.MultiValue selectProps={selectProps} data={data} {...props}>{children}</components.MultiValue>;
    }
    if (index === 2 && values.length > 2) {
        return <div className="controllo-km-ore-select-more-badge">+{values.length - 2}</div>;
    }
    return null;
};

const FullscreenIcon = ({ active }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {active ? (
            <>
                <polyline points="4 14 10 14 10 20"></polyline>
                <polyline points="20 10 14 10 14 4"></polyline>
                <line x1="14" y1="10" x2="21" y2="3"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        ) : (
            <>
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
            </>
        )}
    </svg>
);

const ViewTypeToggle = ({ value, onChange }) => (
    <>
        <button type="button" className={`oper-hr-table-view-btn ${value === "mese" ? "active" : ""}`} onClick={() => onChange("mese")} title="Vista Mensile"><span>M</span></button>
        <button type="button" className={`oper-hr-table-view-btn ${value === "trimestre" ? "active" : ""}`} onClick={() => onChange("trimestre")} title="Vista Trimestrale"><span>T</span></button>
        <button type="button" className={`oper-hr-table-view-btn ${value === "anno" ? "active" : ""}`} onClick={() => onChange("anno")} title="Vista Annuale"><span>A</span></button>
    </>
);

// ── Helper aggregazione ──
// Costruisce dict periodo→{ricavi, ricavi_yoy}.
// rowsY    : righe periodo corrente (anno, mese, ricavi)
// rowsYoY  : righe periodo Y-1; vengono "shiftate" mentalmente di +1 anno in modo che
//            la chiave del bucket coincida col periodo Y a cui sono confrontate.
const buildPeriodi = (rowsY, rowsYoY) => {
    const buckY = {};
    const buckYoY = {};

    const accumula = (target, key, valore) => {
        target[key] = (target[key] || 0) + valore;
    };

    rowsY.forEach(r => {
        const ricavi = Number(r.ricavi) || 0;
        const mKey = `${r.anno}-${String(r.mese).padStart(2, "0")}`;
        const tKey = `${r.anno}-trim${meseToTrimestre[r.mese]}`;
        const yKey = `${r.anno}`;
        [mKey, tKey, yKey, "totale"].forEach(k => accumula(buckY, k, ricavi));
    });

    rowsYoY.forEach(r => {
        const ricavi = Number(r.ricavi) || 0;
        const annoShift = r.anno + 1;
        const mKey = `${annoShift}-${String(r.mese).padStart(2, "0")}`;
        const tKey = `${annoShift}-trim${meseToTrimestre[r.mese]}`;
        const yKey = `${annoShift}`;
        [mKey, tKey, yKey, "totale"].forEach(k => accumula(buckYoY, k, ricavi));
    });

    const periodi = {};
    const allKeys = new Set([...Object.keys(buckY), ...Object.keys(buckYoY)]);
    allKeys.forEach(k => {
        const ricavi = buckY[k];
        const ricavi_y1 = buckYoY[k];
        periodi[k] = {
            ricavi: ricavi != null ? ricavi : null,
            ricavi_yoy: calcYoY(ricavi, ricavi_y1),
        };
    });
    return periodi;
};

// "Show items with no data" stile PowerBI: per la prima dimensione gerarchica,
// elenchiamo sempre questi valori canonici anche quando non hanno match nei dati.
// Utile per evidenziare buchi del DWH (es. macro_voce_analitica oggi tutto NULL).
const EXPECTED_VALUES_BY_CAMPO = {
    macro_voce_analitica: ["Ricavi", "Automezzi", "Contributi", "Costo materiale"],
};

// Costruisce albero gerarchico per riga in base a campiGerarchia.
// Le righe Y-1 alimentano `rowsYoY` del nodo, le Y alimentano `rows`.
const buildAlbero = (transazioni, transazioniYoY, campiGerarchia) => {
    const [c1, c2, c3, c4, c5] = campiGerarchia;
    const radice = { byKey: {} };

    const aggiungi = (r, target) => {
        const vals = [c1, c2, c3, c4, c5].map(c => c ? (r[c] ?? "Non assegnato") : null);
        let nodo = radice;
        for (let i = 0; i < 5; i++) {
            const v = vals[i];
            if (v == null) break;
            if (!nodo.byKey[v]) {
                nodo.byKey[v] = { label: v, rows: [], rowsYoY: [], byKey: {} };
            }
            nodo = nodo.byKey[v];
            nodo[target].push(r);
        }
    };
    transazioni.forEach(r => aggiungi(r, "rows"));
    transazioniYoY.forEach(r => aggiungi(r, "rowsYoY"));

    // Inietta i valori attesi della prima dimensione che non sono già presenti.
    const expected = EXPECTED_VALUES_BY_CAMPO[c1] || [];
    expected.forEach(v => {
        if (!radice.byKey[v]) {
            radice.byKey[v] = { label: v, rows: [], rowsYoY: [], byKey: {} };
        }
    });

    const materializza = (nodo, level, pathKey) => {
        return Object.keys(nodo.byKey).sort().map(k => {
            const n = nodo.byKey[k];
            const id = `${pathKey}/${k}`;
            const periodi = buildPeriodi(n.rows, n.rowsYoY);
            return {
                id, label: n.label, level,
                periodi,
                children: materializza(n, level + 1, id),
            };
        });
    };

    return materializza(radice, 0, "");
};

// Estrae anni e mesi presenti nelle transazioni Y (i periodi visualizzati derivano solo da Y, non da Y-1).
const estraiPeriodiDisponibili = (transazioni) => {
    const anniSet = new Set();
    const mesiPerAnno = {};
    transazioni.forEach(r => {
        anniSet.add(r.anno);
        if (!mesiPerAnno[r.anno]) mesiPerAnno[r.anno] = new Set();
        mesiPerAnno[r.anno].add(r.mese);
    });
    return {
        anni: [...anniSet].sort(),
        mesiPerAnno: Object.fromEntries(
            Object.entries(mesiPerAnno).map(([a, s]) => [a, [...s].sort((x, y) => x - y)])
        ),
    };
};

const Ricavi = ({ filters }) => {
    // Stato dati
    const [transazioni, setTransazioni] = useState([]);
    const [transazioniYoY, setTransazioniYoY] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fullscreen
    const [isFullscreenBar, setIsFullscreenBar] = useState(false);
    const [isFullscreenLine, setIsFullscreenLine] = useState(false);
    const [isFullscreenTable, setIsFullscreenTable] = useState(false);

    // Line chart M/T/A (separato dalla tabella parametrica)
    const [lineViewType, setLineViewType] = useState("mese");

    // Tabella parametrica
    const [selectedColonne, setSelectedColonne] = useState([colonneOptions.find(o => o.value === "trimestre")]);
    const [selectedRighe, setSelectedRighe] = useState([]);
    const [selectedIndicatore, setSelectedIndicatore] = useState([...indicatoreOptions]);
    const [expandedRows, setExpandedRows] = useState(new Set());

    // ── Fetch dataset ──
    useEffect(() => {
        if (!filters?.fromDate || !filters?.toDate) return;
        const controller = new AbortController();
        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("token");
                const settoreCsv = (filters.settore || []).map(s => s.value).join(",");
                const commessaCsv = (filters.commessa || []).map(s => s.value).join(",");
                const clientiCsv = (filters.clienti || []).map(s => s.value).join(",");
                const voceAnaliticaCsv = (filters.voceAnalitica || []).map(s => s.value).join(",");
                const sedeCsv = (filters.sede || []).map(s => s.value).join(",");
                const params = { from: filters.fromDate, to: filters.toDate };
                if (settoreCsv) params.settore = settoreCsv;
                if (commessaCsv) params.commessa = commessaCsv;
                if (clientiCsv) params.cliente_fornitore = clientiCsv;
                if (voceAnaliticaCsv) params.voce_analitica = voceAnaliticaCsv;
                if (sedeCsv) params.sede = sedeCsv;

                const { data } = await axios.get(`${API_URL}/admin/ricavi/dataset`, {
                    params,
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });
                if (cancelled) return;
                setTransazioni(Array.isArray(data?.rows) ? data.rows : []);
                setTransazioniYoY(Array.isArray(data?.rowsYoY) ? data.rowsYoY : []);
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
                if (cancelled) return;
                console.error("Errore caricamento dataset Ricavi:", err);
                setError(err.response?.data?.error || err.message || "Errore caricamento dati");
                setTransazioni([]);
                setTransazioniYoY([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();
        return () => { cancelled = true; controller.abort(); };
    }, [filters]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") {
                setIsFullscreenBar(false);
                setIsFullscreenLine(false);
                setIsFullscreenTable(false);
            }
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, []);

    // ── Aggregazioni derivate dal dataset ──
    const settoreAggregato = useMemo(() => {
        const agg = {};
        transazioni.forEach(r => {
            const k = r.settore || "Non assegnato";
            agg[k] = (agg[k] || 0) + (Number(r.ricavi) || 0);
        });
        return Object.entries(agg).map(([settore, ricavi]) => ({ settore, ricavi }));
    }, [transazioni]);

    const trendMensile = useMemo(() => {
        const agg = {};
        transazioni.forEach(r => {
            const k = `${r.anno}-${String(r.mese).padStart(2, "0")}`;
            if (!agg[k]) agg[k] = { anno: r.anno, mese: r.mese, ricavi: 0 };
            agg[k].ricavi += (Number(r.ricavi) || 0);
        });
        return Object.values(agg).sort((a, b) => (a.anno - b.anno) || (a.mese - b.mese));
    }, [transazioni]);

    // ── Bar chart ──
    // Default (non-fullscreen): tutti i settori visibili, no scroll.
    // Fullscreen: dataZoom slider+inside per scroll verticale (10 visibili alla volta).
    // Sort DESC + yAxis inverse → settori con piu' ricavi in cima.
    const barOptions = useMemo(() => {
        const sorted = [...settoreAggregato].sort((a, b) => b.ricavi - a.ricavi);
        const total = sorted.length;
        const VISIBLE_FULLSCREEN = 10;
        return {
            grid: { left: 150, right: isFullscreenBar ? 90 : 60, top: 20, bottom: 30, containLabel: false },
            xAxis: { type: "value", axisLabel: { formatter: fmtEuroK, fontSize: 11, color: "#64748b" }, splitLine: { lineStyle: { color: "#f1f5f9" } } },
            yAxis: { type: "category", inverse: true, data: sorted.map(d => d.settore), axisLabel: { fontSize: 11, color: "#0f172a" }, axisLine: { show: false }, axisTick: { show: false } },
            dataZoom: isFullscreenBar && total > VISIBLE_FULLSCREEN ? [
                {
                    type: "slider",
                    yAxisIndex: 0,
                    startValue: 0,
                    endValue: VISIBLE_FULLSCREEN - 1,
                    showDetail: false,
                    width: 14,
                    right: 6,
                    brushSelect: false,
                },
                {
                    type: "inside",
                    yAxisIndex: 0,
                    startValue: 0,
                    endValue: VISIBLE_FULLSCREEN - 1,
                    zoomOnMouseWheel: false,
                    moveOnMouseWheel: true,
                },
            ] : undefined,
            tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, formatter: (params) => `<strong>${params[0].name}</strong><br/>Ricavi: ${fmtEuroCompact(params[0].value)}` },
            series: [{
                type: "bar",
                data: sorted.map(d => d.ricavi),
                itemStyle: {
                    color: { type: "linear", x: 0, y: 0, x2: 1, y2: 0, colorStops: [{ offset: 0, color: "#86efac" }, { offset: 1, color: "#22c55e" }] },
                    borderRadius: [0, 4, 4, 0],
                },
                barWidth: 22,
                label: { show: true, position: "right", formatter: (p) => fmtEuroK(p.value), fontSize: 11, color: "#334155", fontWeight: 600 },
            }],
        };
    }, [settoreAggregato, isFullscreenBar]);

    // ── Line chart (con M/T/A) ──
    const lineData = useMemo(() => {
        if (lineViewType === "anno") {
            const byA = {};
            trendMensile.forEach(d => { byA[d.anno] = (byA[d.anno] || 0) + d.ricavi; });
            const anni = Object.keys(byA).sort();
            return { labels: anni.map(String), values: anni.map(a => byA[a]), trimLabels: null };
        }
        if (lineViewType === "trimestre") {
            const byT = {};
            trendMensile.forEach(d => {
                const t = meseToTrimestre[d.mese];
                const k = `${d.anno}-T${t}`;
                byT[k] = (byT[k] || 0) + d.ricavi;
            });
            const keys = Object.keys(byT).sort();
            return {
                labels: keys.map(k => k.replace("-T", " T")),
                values: keys.map(k => byT[k]),
                trimLabels: null,
            };
        }
        return {
            labels: trendMensile.map(d => `${nomiMesi[d.mese - 1]} ${d.anno}`),
            values: trendMensile.map(d => d.ricavi),
            trimLabels: trendMensile.map(d => d.mese % 3 === 2 ? `Trim ${meseToTrimestre[d.mese]}` : ""),
        };
    }, [lineViewType, trendMensile]);

    const lineOptions = useMemo(() => {
        const { labels, values, trimLabels } = lineData;
        const primaryXAxis = {
            type: "category",
            data: labels,
            axisLabel: { fontSize: 11, color: "#64748b" },
            axisLine: { lineStyle: { color: "#e2e8f0" } },
            axisTick: { show: false },
        };
        const opts = {
            grid: { left: 50, right: 40, top: 40, bottom: trimLabels ? 60 : 40, containLabel: false },
            tooltip: { trigger: "axis", formatter: (params) => `<strong>${params[0].name}</strong><br/>Ricavi: ${params[0].value != null ? fmtEuroCompact(params[0].value) : "—"}` },
            xAxis: trimLabels
                ? [primaryXAxis, {
                    type: "category",
                    data: trimLabels,
                    position: "bottom",
                    offset: 22,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: { fontSize: 11, color: "#94a3b8", interval: 0 },
                }]
                : primaryXAxis,
            yAxis: { type: "value", axisLabel: { formatter: fmtEuroK, fontSize: 11, color: "#64748b" }, splitLine: { lineStyle: { color: "#f1f5f9" } } },
            series: [{
                type: "line",
                data: values,
                smooth: false,
                symbol: "circle",
                symbolSize: 8,
                lineStyle: { color: "#22c55e", width: 2 },
                itemStyle: { color: "#22c55e", borderColor: "#fff", borderWidth: 2 },
                areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(34, 197, 94, 0.35)" }, { offset: 1, color: "rgba(34, 197, 94, 0.02)" }] } },
                label: { show: true, position: "top", formatter: (p) => p.value != null ? fmtEuroK(p.value) : "", fontSize: 10, color: "#334155", fontWeight: 600 },
            }],
        };
        return opts;
    }, [lineData]);

    // ── Tabella parametrica ──

    const campiGerarchia = selectedRighe.length > 0 ? selectedRighe.map(r => r.value) : ["macro_voce_analitica"];
    const albero = useMemo(
        () => buildAlbero(transazioni, transazioniYoY, campiGerarchia),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [transazioni, transazioniYoY, campiGerarchia.join("|")]
    );

    const { anni, mesiPerAnno } = useMemo(() => estraiPeriodiDisponibili(transazioni), [transazioni]);
    const totaliGlobali = useMemo(() => buildPeriodi(transazioni, transazioniYoY), [transazioni, transazioniYoY]);

    const haAnnoSelezionato = selectedColonne.some(o => o.value === "anno");
    const haTrimestreSelezionato = selectedColonne.some(o => o.value === "trimestre");
    const haMeseSelezionato = selectedColonne.some(o => o.value === "mese");

    const isMeseValido = (anno, mese) => (mesiPerAnno[anno] || []).includes(mese);
    const isTrimestreValido = (anno, trim) => (mesiPerAnno[anno] || []).some(m => meseToTrimestre[m] === trim);

    const periodiDaMostrare = useMemo(() => {
        if (selectedColonne.length === 0) {
            return [...anni.map(a => ({ chiave: `${a}`, etichetta: `${a}`, anno: a })), { chiave: "totale", etichetta: "Totale", anno: "totale" }];
        }
        const periodi = [];
        anni.forEach(anno => {
            if (haMeseSelezionato) {
                for (let m = 1; m <= 12; m++) {
                    if (isMeseValido(anno, m)) {
                        periodi.push({ chiave: `${anno}-${String(m).padStart(2, "0")}`, etichetta: nomiMesi[m - 1], anno });
                    }
                }
            } else if (haTrimestreSelezionato) {
                for (let t = 1; t <= 4; t++) {
                    if (isTrimestreValido(anno, t)) {
                        periodi.push({ chiave: `${anno}-trim${t}`, etichetta: `T${t}`, anno });
                    }
                }
            } else if (haAnnoSelezionato) {
                periodi.push({ chiave: `${anno}`, etichetta: `${anno}`, anno });
            }
        });
        periodi.push({ chiave: "totale", etichetta: "Totale", anno: "totale" });
        return periodi;
    }, [selectedColonne, anni, mesiPerAnno]); // eslint-disable-line react-hooks/exhaustive-deps

    const isColonnaVisibile = (chiaveInd) => selectedIndicatore.length === 0 ? true : selectedIndicatore.some(o => o.value === chiaveInd);

    const colonnePerPeriodo = useMemo(() => {
        let c = 0;
        if (isColonnaVisibile("ricavi")) c++;
        if (isColonnaVisibile("ricavi_yoy")) c++;
        return c || 1;
    }, [selectedIndicatore]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleRow = (id) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // ── Render metriche per un periodo ──
    const renderMetriche = (periodo, key) => {
        const cells = [];
        if (isColonnaVisibile("ricavi")) {
            cells.push(<td key={`${key}-ricavi`}>{periodo?.ricavi != null ? fmtEuroK(periodo.ricavi) : ""}</td>);
        }
        if (isColonnaVisibile("ricavi_yoy")) {
            const yoy = periodo?.ricavi_yoy;
            const cls = yoy == null ? "" : yoy < 0 ? "value-negative" : "value-positive";
            cells.push(<td key={`${key}-yoy`} className={cls}>{yoy != null ? fmtPerc(yoy) : ""}</td>);
        }
        return cells;
    };

    // ── Render righe (ricorsivo) ──
    const renderNodi = (nodi) => {
        const output = [];
        nodi.forEach(nodo => {
            const haFigli = nodo.children && nodo.children.length > 0;
            const isExpanded = expandedRows.has(nodo.id);
            const icona = haFigli ? (isExpanded ? "▼" : "▶") : "•";
            const classLevel = `tr-level-${nodo.level + 1}`;
            const classCell = `td-settore td-indent-${nodo.level}${haFigli ? " td-expandable" : ""}`;

            output.push(
                <tr key={nodo.id} className={classLevel}>
                    <td className={classCell} onClick={haFigli ? () => toggleRow(nodo.id) : undefined}>
                        <span className="expand-icon">{icona}</span>
                        <span className="level-value">{nodo.label}</span>
                    </td>
                    {periodiDaMostrare.map(p => (
                        <React.Fragment key={p.chiave}>
                            {renderMetriche(nodo.periodi[p.chiave], p.chiave)}
                        </React.Fragment>
                    ))}
                </tr>
            );
            if (isExpanded && haFigli) {
                output.push(...renderNodi(nodo.children));
            }
        });
        return output;
    };

    // ── Export Excel ──
    // Il .xlsx lo genera il backend (controller condiviso `downloadFile`): qui
    // prepariamo solo il payload nel formato "header raggruppati".
    const prepareExportData = () => {
        // Riga 1: un gruppo per periodo, largo quanti indicatori sono visibili.
        // L'anno va esplicitato: su un range multi-anno "Ottobre" da solo è ambiguo.
        const headerGroups = periodiDaMostrare.map(p => ({
            label: p.anno && p.anno !== "totale" && p.etichetta !== String(p.anno)
                ? `${p.etichetta} ${p.anno}`
                : p.etichetta,
            span: colonnePerPeriodo,
        }));

        // Riga 2: sotto-header ripetuti per ogni periodo. La cella [0] porta la gerarchia
        // selezionata — in A1 il controller condiviso scrive "Raggruppamento", hardcoded.
        const headers = [selectedRighe.map(r => r.label).join(" > ") || "Riga"];
        periodiDaMostrare.forEach(() => {
            if (isColonnaVisibile("ricavi")) headers.push("Ricavi");
            if (isColonnaVisibile("ricavi_yoy")) headers.push("YoY");
        });

        // Righe dati: l'albero completo, non solo i nodi espansi a video.
        const rows = [];
        const appendNodo = (nodo) => {
            const cells = ["  ".repeat(nodo.level) + nodo.label];
            periodiDaMostrare.forEach(p => {
                const periodo = nodo.periodi[p.chiave] || {};
                if (isColonnaVisibile("ricavi")) cells.push(numeroPerExcel(periodo.ricavi));
                if (isColonnaVisibile("ricavi_yoy")) cells.push(numeroPerExcel(periodo.ricavi_yoy));
            });
            rows.push(cells);
            if (nodo.children) nodo.children.forEach(appendNodo);
        };
        albero.forEach(appendNodo);

        // Riga finale: il backend mette in grassetto l'ultima riga del foglio.
        const totRow = ["Totale"];
        periodiDaMostrare.forEach(p => {
            const t = totaliGlobali[p.chiave] || {};
            if (isColonnaVisibile("ricavi")) totRow.push(numeroPerExcel(t.ricavi));
            if (isColonnaVisibile("ricavi_yoy")) totRow.push(numeroPerExcel(t.ricavi_yoy));
        });
        rows.push(totRow);

        // Formato cella per colonna, ricavato dai sotto-header appena costruiti.
        const numFmt = headers.map(h => h === "Ricavi" ? FMT_EURO : h === "YoY" ? FMT_PERC : "");

        return { headerGroups, headers, rows, formattazione: { numFmt, negativiRossi: true, freeze: true } };
    };

    const handleExport = async () => {
        try {
            await scaricaExcel({
                endpoint: "admin/ricavi/downloadExcel",
                tableName: "Trend Ricavi",
                datiDaStampare: prepareExportData(),
                filename: "trend_ricavi.xlsx",
            });
        } catch (err) {
            console.error("Errore durante il download del file:", err);
        }
    };

    const labelRighe = selectedRighe.length > 0 ? selectedRighe[0].label : "Macro Voce Analitica";

    if (loading) {
        return (
            <div className="admin-ricavi-container">
                <Lf23Loader />
            </div>
        );
    }

    return (
        <div className="admin-ricavi-container">
            {/* ═══ Row 1: Bar + Line ═══ */}
            <div className="admin-ricavi-grid-2">
                <div className={`admin-ricavi-section${isFullscreenBar ? " fullscreen-trend" : ""}`}>
                    <RotateDevice active={isFullscreenBar} />
                    <div className="admin-ricavi-section-header">
                        <h3 className="admin-ricavi-section-title">Ricavi Per Settore</h3>
                        <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenBar(!isFullscreenBar)} title={isFullscreenBar ? "Esci da tutto schermo" : "Tutto schermo"}>
                            <FullscreenIcon active={isFullscreenBar} />
                        </button>
                    </div>
                    <ReactECharts option={barOptions} style={{ height: isFullscreenBar ? "calc(100vh - 120px)" : "280px", width: "100%" }} notMerge={true} />
                </div>

                <div className={`admin-ricavi-section${isFullscreenLine ? " fullscreen-trend" : ""}`}>
                    <RotateDevice active={isFullscreenLine} />
                    <div className="admin-ricavi-section-header">
                        <h3 className="admin-ricavi-section-title">Trend Ricavi</h3>
                        <div className="oper-hr-table-view-type-container">
                            <ViewTypeToggle value={lineViewType} onChange={setLineViewType} />
                            <button className="trend-fullscreen-btn" onClick={() => setIsFullscreenLine(!isFullscreenLine)} title={isFullscreenLine ? "Esci da tutto schermo" : "Tutto schermo"}>
                                <FullscreenIcon active={isFullscreenLine} />
                            </button>
                        </div>
                    </div>
                    <ReactECharts option={lineOptions} style={{ height: isFullscreenLine ? "calc(100vh - 120px)" : "280px", width: "100%" }} notMerge={true} />
                </div>
            </div>

            {/* ═══ Row 2: Tabella parametrica ═══ */}
            <div className={`controllo-km-ore-table-section${isFullscreenTable ? " fullscreen-table" : ""}`}>
                <RotateDevice active={isFullscreenTable} />

                <div className="controllo-km-ore-table-header">
                    <span className="controllo-km-ore-table-title">Trend Ricavi per Settore</span>
                    <div className="controllo-km-ore-table-header-btn">
                        <button className="controllo-km-ore-download-btn" onClick={handleExport} title="Esporta in Excel" disabled={albero.length === 0}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Esporta
                        </button>
                        <button className="controllo-km-ore-fullscreen-btn" onClick={() => setIsFullscreenTable(!isFullscreenTable)} title={isFullscreenTable ? "Esci da tutto schermo" : "Tutto schermo"}>
                            <FullscreenIcon active={isFullscreenTable} />
                        </button>
                    </div>
                </div>

                {/* Dropdown parametrici */}
                <div className="controllo-km-ore-config-row">
                    <div className="controllo-km-ore-config-item">
                        <label>COLONNE</label>
                        <Select
                            classNamePrefix="controllo-km-ore-select"
                            placeholder="Selezioni multiple"
                            value={selectedColonne}
                            options={colonneOptions}
                            onChange={setSelectedColonne}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{ MultiValue: MultiValueContainer }}
                        />
                    </div>
                    <div className="controllo-km-ore-config-item">
                        <label>RIGHE</label>
                        <Select
                            classNamePrefix="controllo-km-ore-select"
                            placeholder="Selezioni multiple"
                            value={selectedRighe}
                            options={righeOptions}
                            onChange={setSelectedRighe}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{ MultiValue: MultiValueContainer }}
                        />
                    </div>
                    <div className="controllo-km-ore-config-item">
                        <label>INDICATORE</label>
                        <Select
                            classNamePrefix="controllo-km-ore-select"
                            placeholder="Tutti"
                            value={selectedIndicatore}
                            options={indicatoreOptions}
                            onChange={setSelectedIndicatore}
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            components={{ MultiValue: MultiValueContainer }}
                        />
                    </div>
                </div>

                <div className="controllo-km-ore-table-wrapper">
                    <table className="controllo-km-ore-table">
                        <thead>
                            {/* Riga ANNO */}
                            {haAnnoSelezionato && (
                                <tr>
                                    <th className="th-settore">Anno</th>
                                    {(() => {
                                        const celle = [];
                                        anni.forEach(anno => {
                                            let colSpan = colonnePerPeriodo;
                                            if (haMeseSelezionato) {
                                                let mesiValidi = 0;
                                                for (let m = 1; m <= 12; m++) if (isMeseValido(anno, m)) mesiValidi++;
                                                colSpan = mesiValidi * colonnePerPeriodo;
                                            } else if (haTrimestreSelezionato) {
                                                let trimValidi = 0;
                                                for (let t = 1; t <= 4; t++) if (isTrimestreValido(anno, t)) trimValidi++;
                                                colSpan = trimValidi * colonnePerPeriodo;
                                            }
                                            if (colSpan > 0) celle.push(<th key={anno} colSpan={colSpan} className="th-periodo">{anno}</th>);
                                        });
                                        let rowSpanTot = 1;
                                        if (haTrimestreSelezionato) rowSpanTot++;
                                        if (haMeseSelezionato) rowSpanTot++;
                                        celle.push(<th key="tot" colSpan={colonnePerPeriodo} rowSpan={rowSpanTot} className="th-periodo th-totale-header">Totale</th>);
                                        return celle;
                                    })()}
                                </tr>
                            )}
                            {/* Riga TRIMESTRE */}
                            {haTrimestreSelezionato && (
                                <tr>
                                    <th className="th-settore">Trimestre</th>
                                    {(() => {
                                        const celle = [];
                                        const mesiPerTrim = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };
                                        const colSpanTrim = (anno, trim) => {
                                            if (!haMeseSelezionato) return colonnePerPeriodo;
                                            return mesiPerTrim[trim].filter(m => isMeseValido(anno, m)).length * colonnePerPeriodo;
                                        };
                                        anni.forEach(anno => {
                                            for (let t = 1; t <= 4; t++) {
                                                if (isTrimestreValido(anno, t)) {
                                                    celle.push(<th key={`${anno}-t${t}`} colSpan={colSpanTrim(anno, t)} className="th-periodo">Q{t} {anno}</th>);
                                                }
                                            }
                                        });
                                        if (!haAnnoSelezionato) {
                                            let rowSpanTot = 1;
                                            if (haMeseSelezionato) rowSpanTot++;
                                            celle.push(<th key="tot" colSpan={colonnePerPeriodo} rowSpan={rowSpanTot} className="th-periodo th-totale-header">Totale</th>);
                                        }
                                        return celle;
                                    })()}
                                </tr>
                            )}
                            {/* Riga MESE */}
                            {haMeseSelezionato && (
                                <tr>
                                    <th className="th-settore">Mese</th>
                                    {(() => {
                                        const celle = [];
                                        anni.forEach(anno => {
                                            for (let m = 1; m <= 12; m++) {
                                                if (isMeseValido(anno, m)) {
                                                    celle.push(<th key={`${anno}-m${m}`} colSpan={colonnePerPeriodo} className="th-periodo">{nomiMesi[m - 1]} {anno}</th>);
                                                }
                                            }
                                        });
                                        if (!haAnnoSelezionato && !haTrimestreSelezionato) {
                                            celle.push(<th key="tot" colSpan={colonnePerPeriodo} className="th-periodo th-totale-header">Totale</th>);
                                        }
                                        return celle;
                                    })()}
                                </tr>
                            )}
                            {/* Riga INDICATORI (sempre visibile) */}
                            <tr>
                                <th className="th-settore">{labelRighe}</th>
                                {periodiDaMostrare.map(p => {
                                    const cls = p.chiave === "totale" ? "th-metrica th-metrica-totale" : "th-metrica";
                                    return (
                                        <React.Fragment key={p.chiave}>
                                            {isColonnaVisibile("ricavi") && <th className={cls}>Ricavi</th>}
                                            {isColonnaVisibile("ricavi_yoy") && <th className={cls}>Ricavi YoY</th>}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {albero.length > 0 ? renderNodi(albero) : (
                                <tr>
                                    <td className="td-settore" colSpan={100} style={{ textAlign: "center", color: "#94a3b8", fontStyle: "italic", padding: "40px" }}>
                                        {transazioni.length === 0 ? "Nessun dato disponibile" : "Seleziona almeno una dimensione riga per visualizzare i dati"}
                                    </td>
                                </tr>
                            )}
                            {/* Riga Totale */}
                            <tr className="tr-totali">
                                <td className="td-settore"><strong>Totale</strong></td>
                                {periodiDaMostrare.map(p => {
                                    const t = totaliGlobali[p.chiave] || {};
                                    return (
                                        <React.Fragment key={p.chiave}>
                                            {isColonnaVisibile("ricavi") && <td><strong>{t.ricavi != null ? fmtEuroK(t.ricavi) : ""}</strong></td>}
                                            {isColonnaVisibile("ricavi_yoy") && (
                                                <td className={t.ricavi_yoy != null && t.ricavi_yoy < 0 ? "value-negative" : "value-positive"}>
                                                    <strong>{t.ricavi_yoy != null ? fmtPerc(t.ricavi_yoy) : ""}</strong>
                                                </td>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Ricavi;
